import { FieldValues, useController, UseControllerProps } from 'react-hook-form';
import { MinOptionInfo, SelectMenu } from '@stitch-fix/mode-react';
type StringMinOptionInfo = MinOptionInfo<string>;
interface LabeledOption extends StringMinOptionInfo {
  label: string;
}
interface ControlledSelectProps<TOption extends LabeledOption, TFieldValues extends FieldValues> {
  /**
   * The `control` object returned by `useForm`.
   */
  control: UseControllerProps<TFieldValues>['control'];
  /**
   * A key name configured in the corresponding zod schema, i.e.,
   * pass `"selections"` if the zod schema is `z.object({ selections: ... })`.
   */
  name: UseControllerProps<TFieldValues>['name'];
  /**
   * An array of options. Each object must contain a `value` field.
   */
  options: TOption[];
  /**
   * A label value that will serve as the placeholder value prior to selection.
   */
  label?: string;
}

/**
 * This component allows [React Hook Form](https://react-hook-form.com/)
 * to easily communicate with Mode React's [MultiSelectBase](https://mode-react.daylight.stitchfix.com/?path=/docs/base-components-multi-select-base--example)
 */
export const ControlledSelect = <TOption extends LabeledOption, TFieldValues extends FieldValues>({
  control,
  name,
  label,
  options
}: ControlledSelectProps<TOption, TFieldValues>) => {
  const {
    field: {
      onChange,
      value
    }
  } = useController({
    control,
    name
  });
  return <SelectMenu name={name} label={label ?? name} value={value ?? ''} onChange={onChange}>
      <option value="" aria-label="none selected" />
      {options.map(option => <option key={option.label} value={option.value}>
          {option.label}
        </option>)}
    </SelectMenu>;
};